/**
 * PO List Page
 *
 * Displays a paginated list of all purchase orders.
 * Features:
 * - Filters (date range, status, vendor)
 * - Table view with sortable columns including Buyer and Vendor
 * - Pagination
 * - Quick actions (view, archive, delete)
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as purchaseOrderApi from '../api/purchaseOrderApi';
import { getQueueEntryByPO } from '../api/queueApi';
import Button from '../components/common/Button';
import QueueDetailDrawer from '../components/documents/QueueDetailDrawer';
import SparklesIcon from '../components/common/SparklesIcon';
import DateRangePicker from '../components/common/DateRangePicker';
import { formatCurrency } from '../utils/formatCurrency';

/**
 * Status badge component
 */
function StatusBadge({ status }) {
  const colors = {
    ACTIVE: 'bg-green-100 text-green-800',
    ARCHIVED: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colors[status] || colors.ACTIVE}`}>
      {status}
    </span>
  );
}

/**
 * Actions dropdown component
 */
function ActionsDropdown({ po, onArchive, onDelete, isProcessing }) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const openMenu = (e) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 144 }); // 144 = w-36
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={openMenu}
        className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div
            className="fixed w-36 bg-white rounded-md shadow-lg border border-gray-200 z-20"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <div className="py-1">
              {po.status !== 'ARCHIVED' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    onArchive(po._id);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  Archive
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onDelete(po._id);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Sortable column header component
 */
function SortableHeader({ field, label, sortField, sortDirection, onSort, align = 'left' }) {
  const isActive = sortField === field;

  return (
    <th
      className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <span className="flex flex-col">
          <svg
            className={`w-3 h-3 -mb-1 ${isActive && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M5 12l5-5 5 5H5z" />
          </svg>
          <svg
            className={`w-3 h-3 ${isActive && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M5 8l5 5 5-5H5z" />
          </svg>
        </span>
      </div>
    </th>
  );
}

/**
 * POList Component
 */
function POList() {
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  const [processingId, setProcessingId] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [vendorFilter, setVendorFilter] = useState('');
  const [vendors, setVendors] = useState([]);

  // Queue drawer state
  const [selectedQueueEntry, setSelectedQueueEntry] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(null);

  // Sort state
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  /**
   * Format date to YYYY-MM-DD string for API
   */
  const formatDateForApi = (date) => {
    if (!date) return null;
    return date.toISOString().split('T')[0];
  };

  /**
   * Get filter params object
   */
  const getFilterParams = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (dateFrom) params.dateFrom = formatDateForApi(dateFrom);
    if (dateTo) params.dateTo = formatDateForApi(dateTo);
    if (vendorFilter) params.vendor = vendorFilter;
    return params;
  };

  /**
   * Fetch purchase orders
   */
  const fetchPurchaseOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await purchaseOrderApi.listPurchaseOrders({
        page,
        limit: 10,
        ...getFilterParams()
      });

      setPurchaseOrders(response.data || []);
      setTotalPages(response.meta?.totalPages || 1);
      setTotal(response.meta?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch vendors list for dropdown
   */
  const fetchVendors = async () => {
    try {
      const response = await purchaseOrderApi.getVendors();
      setVendors(response.data || []);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  // Fetch vendors on mount
  useEffect(() => {
    fetchVendors();
  }, []);

  // Fetch data when page or filters change
  useEffect(() => {
    fetchPurchaseOrders();
  }, [page, statusFilter, dateFrom, dateTo, vendorFilter]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * Reset all filters
   */
  const resetFilters = () => {
    setStatusFilter('');
    setDateFrom(null);
    setDateTo(null);
    setVendorFilter('');
    setPage(1);
  };

  /**
   * Archive a purchase order
   */
  const handleArchive = async (id) => {
    try {
      setProcessingId(id);
      await purchaseOrderApi.updateStatus(id, 'ARCHIVED');
      setSuccessMessage('Purchase order archived');
      fetchPurchaseOrders();
    } catch (err) {
      setError(err.message || 'Failed to archive purchase order');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Delete a purchase order
   */
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this purchase order? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessingId(id);
      await purchaseOrderApi.deletePurchaseOrder(id);
      setSuccessMessage('Purchase order deleted');
      fetchPurchaseOrders();
    } catch (err) {
      setError(err.message || 'Failed to delete purchase order');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Calculate total amount for a PO
   */
  const calculateTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.unitPrice || 0));
    }, 0);
  };

  /**
   * Get party name by type
   */
  const getPartyName = (parties, type) => {
    if (!parties || parties.length === 0) return 'N/A';
    const party = parties.find(p => p.type === type);
    return party?.name || 'N/A';
  };

  /**
   * Open the queue detail drawer for a PO
   */
  const handleOpenDrawer = async (poId, e) => {
    e.stopPropagation();
    if (drawerLoading === poId) return;
    setDrawerLoading(poId);
    try {
      const response = await getQueueEntryByPO(poId);
      setSelectedQueueEntry(response.data);
      setIsDrawerOpen(true);
    } catch {
      setSelectedQueueEntry(null);
      setIsDrawerOpen(false);
    } finally {
      setDrawerLoading(null);
    }
  };

  const hasActiveFilters = statusFilter || dateFrom || dateTo || vendorFilter;

  /**
   * Handle column header click for sorting
   */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Get sorted purchase orders
   */
  const getSortedPurchaseOrders = () => {
    const sorted = [...purchaseOrders].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'poNumber':
          aValue = a.header?.poNumber || '';
          bValue = b.header?.poNumber || '';
          break;
        case 'date':
          aValue = a.header?.date ? new Date(a.header.date).getTime() : 0;
          bValue = b.header?.date ? new Date(b.header.date).getTime() : 0;
          break;
        case 'buyer':
          aValue = getPartyName(a.parties, 'BUYER').toLowerCase();
          bValue = getPartyName(b.parties, 'BUYER').toLowerCase();
          break;
        case 'vendor':
          aValue = getPartyName(a.parties, 'VENDOR').toLowerCase();
          bValue = getPartyName(b.parties, 'VENDOR').toLowerCase();
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'items':
          aValue = a.items?.length || 0;
          bValue = b.items?.length || 0;
          break;
        case 'total':
          aValue = calculateTotal(a.items);
          bValue = calculateTotal(b.items);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedPurchaseOrders = getSortedPurchaseOrders();

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-600">{total} total orders</p>
        </div>
        <Link to="/purchase-orders/ingest">
          <Button>+ New PO Upload</Button>
        </Link>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="ml-2 text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Date Range */}
          <DateRangePicker
            startDate={dateFrom}
            endDate={dateTo}
            onChange={({ startDate, endDate }) => {
              setDateFrom(startDate);
              setDateTo(endDate);
              setPage(1);
            }}
          />

          {/* Status Filter */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>

          {/* Vendor Filter */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Vendor</label>
            <select
              value={vendorFilter}
              onChange={(e) => {
                setVendorFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[150px]"
            >
              <option value="">All Vendors</option>
              {vendors.map((vendor) => (
                <option key={vendor} value={vendor}>
                  {vendor}
                </option>
              ))}
            </select>
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : purchaseOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-4">No purchase orders found</p>
            {hasActiveFilters ? (
              <button
                onClick={resetFilters}
                className="mt-2 text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            ) : (
              <Link to="/purchase-orders/ingest" className="mt-2 text-blue-600 hover:underline inline-block">
                Upload your first PO
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader
                    field="poNumber"
                    label="PO Number"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="date"
                    label="Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="buyer"
                    label="Buyer"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="vendor"
                    label="Vendor"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="status"
                    label="Status"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="items"
                    label="Items"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="total"
                    label="Total"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  />
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPurchaseOrders.map((po) => (
                  <tr
                    key={po._id}
                    className={`hover:bg-gray-50 cursor-pointer ${
                      isDrawerOpen && selectedQueueEntry?.purchaseOrder === po._id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => navigate(`/purchase-orders/${po._id}`)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-blue-600 hover:text-blue-800">
                        {po.header?.poNumber || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(po.header?.date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {getPartyName(po.parties, 'BUYER')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {getPartyName(po.parties, 'VENDOR')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={po.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {po.items?.length || 0} items
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(calculateTotal(po.items), po.header?.currency)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleOpenDrawer(po._id, e)}
                          className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                            isDrawerOpen && selectedQueueEntry?.purchaseOrder === po._id
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-gray-400 hover:text-gray-600'
                          }`}
                          title="AI extraction details"
                        >
                          {drawerLoading === po._id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                          ) : (
                            <SparklesIcon className="w-5 h-5" />
                          )}
                        </button>
                        <ActionsDropdown
                          po={po}
                          onArchive={handleArchive}
                          onDelete={handleDelete}
                          isProcessing={processingId === po._id}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Overlay drawer for queue details */}
      <QueueDetailDrawer
        open={isDrawerOpen && !!selectedQueueEntry}
        queueEntry={selectedQueueEntry}
        confidence={selectedQueueEntry?.confidence}
        onClose={() => { setIsDrawerOpen(false); setSelectedQueueEntry(null); }}
      />
    </div>
  );
}

export default POList;
